import { z } from "zod";
import {
  DRIVEWAY_DIFFICULTY_MAX,
  DRIVEWAY_DIFFICULTY_MIN,
} from "service/types/enum/driveway-enum";

const DIFFICULTY_MESSAGE = `Enter a difficulty from ${DRIVEWAY_DIFFICULTY_MIN} to ${DRIVEWAY_DIFFICULTY_MAX}`;

// Frontend-only validation for the CRM fields that the generated (OpenAPI) Zod
// schemas leave as bare optionals. Spread these into a form's schema via
// `.extend` so a value is validated when present, while the field stays
// optional to match the backend spec. The generated schemas are never
// hand-edited (regenerated via `yarn generate:api`), so these live here and are
// layered on per form.
//
// Every CRM form (leads, convert-to-customer, and the customer address forms)
// holds these fields flat in its Formik values and only nests them under
// `locationDetails` at submit time, so validating the flat keys lines up with
// the values Formik actually checks.
export const crmFieldValidations = {
  onsiteContactEmail: z
    .union([z.string().email("Invalid email"), z.literal("")])
    .optional(),
  contractPrice: z
    .number({ invalid_type_error: "Enter a valid price" })
    .nonnegative("Price can't be negative")
    .multipleOf(0.01, "Price can have at most 2 decimal places")
    .optional(),
  // Decimal-backed on the backend (the request DTO is a plain `number`), so a
  // fractional measurement is allowed — only the sign is enforced.
  squareFootage: z
    .number({ invalid_type_error: "Enter a valid square footage" })
    .positive("Enter a valid square footage")
    .optional(),
  difficulty: z
    .number({ invalid_type_error: DIFFICULTY_MESSAGE })
    .int(DIFFICULTY_MESSAGE)
    .min(DRIVEWAY_DIFFICULTY_MIN, DIFFICULTY_MESSAGE)
    .max(DRIVEWAY_DIFFICULTY_MAX, DIFFICULTY_MESSAGE)
    .optional(),
};

// Non-empty enforcement for the required name fields. The generated schemas
// type these as bare `z.string()`, which accepts "" — so the required (*)
// fields don't actually block an empty submission without this.
export const requiredNameValidations = {
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
};
